/**
 * ActionRow — flat-JSX recursive action editor for Status Workflows
 * Depth 0 = Action, 1 = Sub-action, 2 = Sub-sub-action (max).
 * Kept JSX shallow (≤4 levels per element) so the visual-edits
 * babel plugin doesn't blow its traversal stack on this recursive tree.
 */
import React, { useState } from 'react';
import PromptListEditor from './PromptListEditor';

const CH_DOWN = '▾';
const CH_RIGHT = '▸';

function newId() {
  return (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : ('id-' + Math.random().toString(36).slice(2, 12));
}

function ParamField(props) {
  const { name, type, value, onChange, testid } = props;
  if (type === 'text') {
    return React.createElement('textarea', {
      value: value == null ? '' : value,
      onChange: e => onChange(e.target.value),
      'data-testid': testid,
      rows: 2,
      className: 'w-full text-xs px-2 py-1 border border-slate-200 rounded font-mono',
    });
  }
  if (type === 'boolean') {
    return React.createElement('label', { className: 'text-xs flex items-center gap-2' },
      React.createElement('input', {
        type: 'checkbox',
        checked: !!value,
        onChange: e => onChange(e.target.checked),
        'data-testid': testid,
      }),
      React.createElement('span', { className: 'text-slate-600' }, 'enabled'),
    );
  }
  return React.createElement('input', {
    value: value == null ? '' : value,
    onChange: e => onChange(e.target.value),
    'data-testid': testid,
    className: 'w-full text-xs px-2 py-1 border border-slate-200 rounded font-mono',
  });
}

export default function ActionRow(props) {
  const {
    action, depth, primitives, onChange, onDelete, onMove, canMoveUp, canMoveDown,
  } = props;
  const primMeta = primitives.find(p => p.key === action.type);
  const paramSchema = primMeta ? (primMeta.param_schema || {}) : {};
  const [expanded, setExpanded] = useState(depth === 0);
  const subList = action.sub_actions || [];
  const canNest = depth < 2;

  const setField = patch => onChange(Object.assign({}, action, patch));
  const setParam = (name, val) => setField({ params: Object.assign({}, action.params, { [name]: val }) });
  const addSub = () => setField({
    sub_actions: subList.concat({
      id: newId(), order: (subList.length + 1) * 10,
      type: 'checkbox_tick', params: { label: 'New sub-action' },
      mode: 'manual', sub_actions: [], wait_state: null,
    }),
  });
  const setSub = (idx, next) => {
    const arr = subList.slice();
    if (next === null) arr.splice(idx, 1);
    else arr[idx] = next;
    setField({ sub_actions: arr });
  };
  const moveSub = (idx, dir) => {
    const arr = subList.slice();
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    const tmp = arr[idx]; arr[idx] = arr[j]; arr[j] = tmp;
    setField({ sub_actions: arr });
  };

  const indentClass = depth === 0
    ? 'border-l-4 border-sky-300 bg-sky-50/40'
    : depth === 1
      ? 'border-l-4 border-indigo-300 bg-indigo-50/40'
      : 'border-l-4 border-purple-300 bg-purple-50/40';
  const badgeCls = depth === 0
    ? 'bg-sky-50 text-sky-700 border-sky-200'
    : depth === 1
      ? 'bg-purple-50 text-purple-700 border-purple-200'
      : 'bg-orange-50 text-orange-700 border-orange-200';
  const badgeLabel = depth === 0 ? 'action' : (depth === 1 ? 'sub-action' : 'sub-sub-action');
  const subLabel = depth === 0 ? 'sub-action' : 'sub-sub-action';
  const testidPrefix = 'action-' + action.id.slice(0, 8);
  const paramEntries = Object.entries(paramSchema);

  return React.createElement('div', {
    className: 'rounded px-3 py-2 mb-2 ' + indentClass,
    'data-testid': testidPrefix,
  },
    renderHeader({
      testidPrefix, expanded, setExpanded, action, setField, primitives,
      badgeCls, badgeLabel, onMove, onDelete, canMoveUp, canMoveDown,
    }),
    primMeta && primMeta.description
      ? React.createElement('div', { className: 'text-[11px] text-slate-500 italic mt-1' }, primMeta.description)
      : null,
    expanded ? renderBody({
      paramEntries, action, setParam, testidPrefix, canNest, subList, subLabel,
      addSub, primitives, setSub, moveSub, depth,
    }) : null,
  );
}

function renderHeader(ctx) {
  const {
    testidPrefix, expanded, setExpanded, action, setField, primitives,
    badgeCls, badgeLabel, onMove, onDelete, canMoveUp, canMoveDown,
  } = ctx;
  return React.createElement('div', { className: 'flex items-center gap-2 flex-wrap' },
    React.createElement('button', {
      onClick: () => setExpanded(v => !v),
      'data-testid': testidPrefix + '-toggle',
      className: 'text-slate-400 hover:text-slate-600 font-mono text-xs',
    }, expanded ? CH_DOWN : CH_RIGHT),
    React.createElement('select', {
      value: action.type,
      onChange: e => setField({ type: e.target.value, params: {} }),
      'data-testid': testidPrefix + '-type',
      className: 'text-xs px-2 py-1 border border-slate-300 rounded bg-white font-mono min-w-[220px]',
    }, primitives.map(p => React.createElement('option', { key: p.key, value: p.key },
      p.group + ' · ' + p.label))),
    React.createElement('select', {
      value: action.mode || 'auto',
      onChange: e => setField({ mode: e.target.value }),
      'data-testid': testidPrefix + '-mode',
      className: 'text-xs px-2 py-1 border border-slate-300 rounded bg-white',
    },
      React.createElement('option', { value: 'auto' }, 'auto'),
      React.createElement('option', { value: 'manual' }, 'manual'),
      React.createElement('option', { value: 'mixed' }, 'mixed'),
    ),
    React.createElement('span', {
      className: 'text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded border ' + badgeCls,
    }, badgeLabel),
    React.createElement('span', { className: 'flex-1' }),
    React.createElement('button', {
      onClick: () => onMove(-1), disabled: !canMoveUp, title: 'Move up',
      'data-testid': testidPrefix + '-up',
      className: 'p-1 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-30 text-xs',
    }, '↑'),
    React.createElement('button', {
      onClick: () => onMove(1), disabled: !canMoveDown, title: 'Move down',
      'data-testid': testidPrefix + '-down',
      className: 'p-1 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-30 text-xs',
    }, '↓'),
    React.createElement('button', {
      onClick: onDelete, title: 'Delete',
      'data-testid': testidPrefix + '-delete',
      className: 'p-1 rounded hover:bg-rose-50 text-rose-500 text-xs',
    }, '✕'),
  );
}

function renderBody(ctx) {
  const {
    paramEntries, action, setParam, testidPrefix, canNest, subList, subLabel,
    addSub, primitives, setSub, moveSub, depth,
  } = ctx;
  return React.createElement('div', null,
    paramEntries.length > 0 ? renderParams({ paramEntries, action, setParam, testidPrefix }) : null,
    canNest ? renderSubs({
      subList, subLabel, addSub, testidPrefix, primitives, setSub, moveSub, depth,
    }) : null,
  );
}

function renderParams(ctx) {
  const { paramEntries, action, setParam, testidPrefix } = ctx;
  const params = action.params || {};
  const isIntake = action.type === 'open_intake_form' || action.type === 'open_property_intake_form';
  const promptCategory = action.type === 'open_property_intake_form' ? 'property' : 'client';
  return React.createElement('div', { className: 'mt-2 grid grid-cols-2 gap-2' },
    paramEntries.map(entry => {
      const name = entry[0];
      const type = entry[1];
      // Swap the plain textarea for the PromptListEditor when we're editing
      // an intake-form primitive's `prompts` param.
      if (isIntake && name === 'prompts') {
        return React.createElement('div', { key: name, className: 'col-span-2' },
          React.createElement('label', { className: 'text-[10px] uppercase tracking-wide text-slate-500 mb-1 block' },
            'prompts · question script (order + text drive the runner form)'),
          React.createElement(PromptListEditor, {
            value: params[name],
            onChange: v => setParam(name, v),
            category: promptCategory,
            testidPrefix: testidPrefix + '-prompts',
          }),
        );
      }
      return React.createElement('div', { key: name },
        React.createElement('label', { className: 'text-[10px] uppercase tracking-wide text-slate-500' },
          name + ' (' + type + ')'),
        React.createElement(ParamField, {
          name, type,
          value: params[name],
          onChange: v => setParam(name, v),
          testid: testidPrefix + '-param-' + name,
        }),
      );
    }),
  );
}

function renderSubs(ctx) {
  const { subList, subLabel, addSub, testidPrefix, primitives, setSub, moveSub, depth } = ctx;
  const hasSubs = subList.length > 0;
  return React.createElement('div', { className: 'mt-3' },
    React.createElement('div', { className: 'flex items-center gap-2 mb-1' },
      React.createElement('span', {
        className: 'text-[11px] uppercase tracking-wide font-semibold text-slate-500',
      }, hasSubs
        ? (subList.length + ' ' + subLabel + (subList.length > 1 ? 's' : ''))
        : ('+ Add ' + subLabel)),
      React.createElement('button', {
        onClick: addSub,
        'data-testid': testidPrefix + '-add-sub',
        className: 'text-[10px] px-2 py-0.5 rounded border border-slate-300 hover:bg-slate-100',
      }, '+ Add ' + subLabel),
    ),
    subList.map((sub, idx) => React.createElement(ActionRow, {
      key: sub.id,
      action: sub,
      depth: depth + 1,
      primitives,
      onChange: next => setSub(idx, next),
      onDelete: () => setSub(idx, null),
      onMove: dir => moveSub(idx, dir),
      canMoveUp: idx > 0,
      canMoveDown: idx < (subList.length - 1),
    })),
  );
}
